import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { conectarChatElite, liberarChatElite } from "../services/chatElite.socket";
import { getPaginaActual } from "../utilities/chatEliteContexto";

/**
 * Conexion al socket del Chat Elite.
 * Si `enviarEstado` (lado AAFF), informa en tiempo real de la pagina en la que esta, si la pestaña
 * esta visible y si tiene el chat/ticket abierto (el equipo lo ve en el panel).
 *
 * @param {Object} params
 * @param {boolean} params.activo - Conectar o no (p.ej. solo AAFF Elite, ADMIN y CONSUL)
 * @param {boolean} [params.enviarEstado=false]
 * @param {boolean} [params.chatAbierto=false]
 * @param {number|null} [params.ticketAbierto=null]
 * @returns {{ socket: import("socket.io-client").Socket | null, conectado: boolean }}
 */
const useChatEliteSocket = ({ activo, enviarEstado = false, chatAbierto = false, ticketAbierto = null }) => {
  const location = useLocation();
  const [socket, setSocket] = useState(null);
  const [conectado, setConectado] = useState(false);
  const estadoRef = useRef({});

  const token = activo ? localStorage.getItem("token") : null;

  const getEstado = () => estadoRef.current;

  useEffect(() => {
    if (!token) return;

    const s = conectarChatElite(token, enviarEstado ? getEstado : null);
    const onConnect = () => setConectado(true);
    const onDisconnect = () => setConectado(false);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    setSocket(s);
    setConectado(s.connected);

    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      liberarChatElite();
      setSocket(null);
      setConectado(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Estado del AAFF: pagina, visibilidad y chat abierto
  useEffect(() => {
    if (!enviarEstado) return;

    const emitirEstado = () => {
      estadoRef.current = {
        pagina: getPaginaActual(),
        visible: document.visibilityState !== "hidden",
        chatAbierto,
        ticketAbierto,
      };
      if (socket?.connected) socket.emit("chatElite:estado", estadoRef.current);
    };

    emitirEstado();
    document.addEventListener("visibilitychange", emitirEstado);
    socket?.on("connect", emitirEstado);
    return () => {
      document.removeEventListener("visibilitychange", emitirEstado);
      socket?.off("connect", emitirEstado);
    };
  }, [enviarEstado, socket, location.pathname, location.search, chatAbierto, ticketAbierto]);

  return { socket, conectado };
};

export default useChatEliteSocket;
